// values can be missing or (when injected by webpack DefinePlugin) not strings
const isTrue = (val: unknown): boolean => {
  if (val === null || val === undefined) return false
  if (typeof val === 'string') return ['true', '1'].includes(val.trim().toLocaleLowerCase())
  return val === true || val === 1
}

const getJson = (val: string | undefined): unknown => {
  if (!val) return undefined
  try {
    return JSON.parse(val)
  } catch {
    return undefined
  }
}

const environments = {
  development: 'development',
  production: 'production',
} as const

export const ENV = {
  arenaRoot: process.env.ARENA_ROOT,
  arenaDist: process.env.ARENA_DIST,
  arenaPort: process.env.PORT || process.env.ARENA_PORT || '9090',
  nodeEnv: process.env.NODE_ENV || environments.development,
  debug: isTrue(process.env.DEBUG),
  tempFolder: process.env.TEMP_FOLDER || '/tmp/arena_upload',
  buildReport: isTrue(process.env.BUILD_REPORT),
  // APP VERSION
  applicationVersion: process.env.APP_VERSION,
  // EMAIL
  emailService: process.env.EMAIL_SERVICE || 'sendgrid',
  emailFrom: process.env.EMAIL_FROM,
  emailAuthUser: process.env.EMAIL_AUTH_USER,
  emailAuthPassword: process.env.EMAIL_AUTH_PASSWORD,
  emailTransportOptions: getJson(process.env.EMAIL_TRANSPORT_OPTIONS),
  // the actual sender address used when sending emails (see server/utils/mailer/mailer.js);
  // EMAIL_FROM_RESOLVED is only ever set by the client webpack bundle (see webpack.config.mjs),
  // where it carries this same resolved value across to the browser
  emailFromResolved:
    process.env.EMAIL_FROM_RESOLVED || process.env.EMAIL_FROM || process.env.ADMIN_EMAIL || process.env.EMAIL_AUTH_USER,
  // address shown to users in emails, to contact in case of problems (e.g. an access request they didn't make)
  supportEmail: process.env.SUPPORT_EMAIL || process.env.ADMIN_EMAIL,
  sendGridApiKey: process.env.SENDGRID_API_KEY,
  emailAmazonSESHost: process.env.EMAIL_AMAZON_SES_HOST,
  emailAmazonSESPort: Number(process.env.EMAIL_AMAZON_SES_PORT) || 465,
  // ANALYSIS
  analysisOutputDir: process.env.ANALYSIS_OUTPUT_DIR,
  // SERVER
  useHttps: isTrue(process.env.USE_HTTPS),
  // Public origin baked into QR codes / emails when set (no trailing slash). Falls back to request Host.
  arenaPublicUrl: process.env.ARENA_PUBLIC_URL || '',
  fileUploadLimit: Number(process.env.FILE_UPLOAD_LIMIT) || 1024 ** 3, // 1GB
  // RStudio Server
  rStudioDownloadServerUrl: process.env.RSTUDIO_DOWNLOAD_SERVER_URL,
  rStudioServerUrl: process.env.RSTUDIO_SERVER_URL,
  rStudioPoolServerURL: process.env.RSTUDIO_POOL_SERVER_URL,
  rStudioPoolServiceKey: process.env.RSTUDIO_POOL_SERVICE_KEY,
  // Arena Mobile web build used for the mobile preview in the survey designer (preview hidden when not set):
  // /mobile-preview/ when downloaded by the build (yarn mobile-preview:download)
  mobilePreviewUrl: process.env.MOBILE_PREVIEW_URL,
  // ReCaptcha
  reCaptchaEnabled: isTrue(process.env.RECAPTCHA_ENABLED),
  reCaptchaSiteKey: process.env.RECAPTCHA_SITE_KEY,
  reCaptchaSecretKey: process.env.RECAPTCHA_SECRET_KEY,
  // Map
  mapApiKeyPlanet: process.env.MAP_API_KEY_PLANET,
  // SYSTEM ADMIN USER
  // - admin email address
  //   used to create default system admin user when DB is empty
  //   and to send emails to the users)
  adminEmail: process.env.ADMIN_EMAIL,
  // - admin user password
  adminPassword: process.env.ADMIN_PASSWORD,
  allowUserAccessRequest: isTrue(process.env.ALLOW_USER_ACCESS_REQUEST ?? 'true'),
  // FILE STORAGE
  fileStoragePath: process.env.FILE_STORAGE_PATH,
  fileStorageAwsAccessKey: process.env.FILE_STORAGE_AWS_ACCESS_KEY,
  fileStorageAwsSecretAccessKey: process.env.FILE_STORAGE_AWS_SECRET_ACCESS_KEY,
  fileStorageAwsS3BucketName: process.env.FILE_STORAGE_AWS_S3_BUCKET_NAME,
  fileStorageAwsS3BucketRegion: process.env.FILE_STORAGE_AWS_S3_BUCKET_REGION,
  // Job queue
  jobQueueConcurrency: process.env.JOB_QUEUE_CONCURRENCY || 3,
  // WHISP
  whispApiKey: process.env.WHISP_API_KEY,
  // Activity log
  activityLogDisabled: isTrue(process.env.ACTIVITY_LOG_DISABLED),
  // HTTP requests log (always enabled in development)
  logHttpRequests: isTrue(process.env.LOG_HTTP_REQUESTS),
  // Experimental features
  experimentalFeatures: isTrue(process.env.EXPERIMENTAL_FEATURES),
  // ===== AI =====
  // Master kill switch for all AI features
  aiFeaturesEnabled: isTrue(process.env.AI_FEATURES_ENABLED ?? 'false'),
  // Per-feature flags as a JSON object, e.g.
  // {"translation":true,"expressionGenerate":true,"expressionExplain":true,"activityLogSummary":true,"dataDictionary":true}
  aiFeatureFlags: getJson(process.env.AI_FEATURE_FLAGS),
  // Default provider (used when user has no override)
  aiDefaultProvider: process.env.AI_DEFAULT_PROVIDER, // openai | anthropic | google | openai-compatible
  aiDefaultModel: process.env.AI_DEFAULT_MODEL,
  aiDefaultApiKey: process.env.AI_DEFAULT_API_KEY,
  aiDefaultBaseUrl: process.env.AI_DEFAULT_BASE_URL,
  // Master encryption key for user-stored API keys (32-byte hex)
  aiUserKeyEncryptionSecret: process.env.AI_USER_KEY_ENCRYPTION_SECRET,
  // Limits
  aiMaxPromptChars: Number(process.env.AI_MAX_PROMPT_CHARS) || 200000,
  aiRequestTimeoutMs: Number(process.env.AI_REQUEST_TIMEOUT_MS) || 60000,
}

export const isEnvDevelopment = ENV.nodeEnv === environments.development
export const isEnvProduction = ENV.nodeEnv === environments.production
